import WYATTElement from "wyatt";

const STORE_DATETIME_PATTERN = /^(.+?)(?:\[([^\]]+)\])?$/;

export default class StoreHours extends WYATTElement {
  get special_hours() {
    return this.config.t[this.today?.hour_type] ?? "";
  }

  get openingHours() {
    return this.config.opening_hours ?? [];
  }

  get locale() {
    return document.documentElement.lang || "en";
  }

  get timeZoneLong() {
    const datetime =
      this.openingHours[0]?.open_datetime ||
      this.openingHours[0]?.close_datetime;

    return datetime?.match(STORE_DATETIME_PATTERN)?.[2];
  }

  get timeZoneShort() {
    if (!this.timeZoneLong) {
      return undefined;
    }

    const part = new Intl.DateTimeFormat("en-US", {
      timeZone: this.timeZoneLong,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find(({ type }) => type === "timeZoneName");

    return part?.value;
  }

  get today() {
    if (!this.timeZoneLong) {
      return null;
    }

    const storeDate = new Intl.DateTimeFormat("en-CA", {
      timeZone: this.timeZoneLong,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());

    return this.openingHours.find((entry) => entry.date === storeDate) ?? null;
  }

  isOpenDay(entry) {
    if (!entry?.open_datetime || !entry?.close_datetime) {
      return false;
    }

    const openMatch = entry.open_datetime.match(STORE_DATETIME_PATTERN);
    const closeMatch = entry.close_datetime.match(STORE_DATETIME_PATTERN);

    if (!openMatch || !closeMatch) {
      return false;
    }

    const open = new Date(openMatch[1]).getTime();
    const close = new Date(closeMatch[1]).getTime();

    if (Number.isNaN(open) || Number.isNaN(close)) {
      return false;
    }

    return open < close;
  }

  get isOpen() {
    const entry = this.today;

    if (!this.isOpenDay(entry)) {
      return false;
    }

    const openMatch = entry.open_datetime.match(STORE_DATETIME_PATTERN);
    const closeMatch = entry.close_datetime.match(STORE_DATETIME_PATTERN);
    const now = Date.now();
    const open = new Date(openMatch[1]).getTime();
    const close = new Date(closeMatch[1]).getTime();

    return now >= open && now < close;
  }

  get nextClosingTime() {
    if (!this.isOpen) {
      return null;
    }

    const { close_datetime: datetime } = this.today ?? {};

    if (!datetime || !this.timeZoneLong) {
      return null;
    }

    const match = datetime.match(STORE_DATETIME_PATTERN);

    if (!match) {
      return null;
    }

    const [, isoPart] = match;
    const date = new Date(isoPart);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const isFrench = this.locale.startsWith("fr");

    return new Intl.DateTimeFormat(this.locale, {
      timeZone: this.timeZoneLong,
      hour: isFrench ? "2-digit" : "numeric",
      minute: "2-digit",
      hour12: !isFrench,
    }).format(date);
  }

  get nextOpening() {
    if (this.isOpen) {
      return null;
    }

    const now = Date.now();
    const { today } = this;

    if (this.isOpenDay(today)) {
      const match = today.open_datetime.match(STORE_DATETIME_PATTERN);
      const open = new Date(match[1]).getTime();

      if (now < open) {
        return today;
      }
    }

    const startIndex = today ? this.openingHours.indexOf(today) + 1 : 0;

    for (let index = startIndex; index < this.openingHours.length; index += 1) {
      const entry = this.openingHours[index];

      if (this.isOpenDay(entry)) {
        const match = entry.open_datetime.match(STORE_DATETIME_PATTERN);
        const open = new Date(match[1]).getTime();

        if (now < open) {
          return entry;
        }
      }
    }

    return null;
  }

  get nextOpeningDay() {
    const { open_datetime: datetime } = this.nextOpening ?? {};

    if (!datetime || !this.timeZoneLong) {
      return null;
    }

    const match = datetime.match(STORE_DATETIME_PATTERN);

    if (!match) {
      return null;
    }

    const [, isoPart] = match;
    const date = new Date(isoPart);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return new Intl.DateTimeFormat(this.locale, {
      timeZone: this.timeZoneLong,
      weekday: "long",
    }).format(date);
  }

  get nextOpeningTime() {
    const { open_datetime: datetime } = this.nextOpening ?? {};

    if (!datetime || !this.timeZoneLong) {
      return null;
    }

    const match = datetime.match(STORE_DATETIME_PATTERN);

    if (!match) {
      return null;
    }

    const [, isoPart] = match;
    const date = new Date(isoPart);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const isFrench = this.locale.startsWith("fr");

    return new Intl.DateTimeFormat(this.locale, {
      timeZone: this.timeZoneLong,
      hour: isFrench ? "2-digit" : "numeric",
      minute: "2-digit",
      hour12: !isFrench,
    }).format(date);
  }

  get closedUntil() {
    if (this.isOpen || !this.nextOpeningDay || !this.nextOpeningTime) {
      return null;
    }

    return {
      day: this.nextOpeningDay,
      time: this.nextOpeningTime,
      entry: this.nextOpening,
    };
  }
}
